import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";

const ProfileSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [profile, setProfile] = useState({
    full_name: "",
    email: "",
    phone: "",
    unit_number: "",
    street_number: "",
    street_name: "",
    suburb: "",
    state: "",
    postcode: "",
  });
  const [showPhoneFormatTips, setShowPhoneFormatTips] = useState(false);

  // Helper function to sanitize phone numbers
  const sanitizePhoneNumber = (phone: string) => {
    return phone.replace(/\D/g, ''); // Remove all non-digit characters
  };

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        const { data: userData, error: userError } = await supabase
          .from('customer_profiles')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (userError && userError.code !== 'PGRST116') {
          throw userError;
        }
        
        // Always use empty strings instead of null for form fields
        setProfile({
          full_name: user.user_metadata?.full_name || "",
          email: user.email || "",
          phone: userData?.phone || "",
          unit_number: userData?.unit_number || "",
          street_number: userData?.street_number || "",
          street_name: userData?.street_name || "",
          suburb: userData?.suburb || "",
          state: userData?.state || "",
          postcode: userData?.postcode || "",
        });
      } catch (error) {
        console.error("Error fetching profile:", error);
        const errorMsg = error instanceof Error ? error.message : "Unknown error";
        setErrorMessage(`Error loading profile data: ${errorMsg}`);
        toast.error("Error loading profile data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [user]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'phone' || name === 'postcode') {
      // Only allow numbers for phone and postcode
      const numericValue = value.replace(/\D/g, '');
      setProfile(prev => ({ ...prev, [name]: numericValue }));
    } else {
      // For all other fields, just store as is
      setProfile(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !user.id) {
      console.error("User or user ID is not available. Cannot update profile.");
      setErrorMessage("Your session might have expired. Please try refreshing or logging in again.");
      toast.error("Cannot update profile: User information missing.");
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setErrorMessage("");
      
      // Update all fields in one operation - include full_name and email
      const updateData = {
        id: user.id,
        full_name: profile.full_name || "",
        email: user.email || "",
        unit_number: profile.unit_number || "",
        street_number: profile.street_number || "",
        street_name: profile.street_name || "",
        suburb: profile.suburb || "",
        state: profile.state || "",
        postcode: profile.postcode || "",
        // Store phone as a simple string
        phone: profile.phone || "",
        updated_at: new Date().toISOString(),
      };
      
      console.log("Updating profile with data:", updateData);
      
      // Single update operation for all fields
      const { error: updateError } = await supabase
        .from('customer_profiles')
        .upsert(updateData);
      
      if (updateError) {
        console.error("Error updating profile:", updateError);
        setErrorMessage(`Failed to update profile: ${updateError.message}`);
        return;
      }
      
      // Update Auth metadata
      const { error: userError } = await supabase.auth.updateUser({
        data: { full_name: profile.full_name }
      });
      
      if (userError) {
        console.error("User metadata update error:", userError);
        setErrorMessage(`Profile updated but failed to update name: ${userError.message}`);
        return;
      }
      
      toast.success("Profile updated successfully");
      
      // Refresh the data
      const { data: refreshedData } = await supabase
        .from('customer_profiles')
        .select('*')
        .eq('id', user.id)
        .single();
        
      if (refreshedData) {
        // Always use empty strings for null values
        setProfile(prev => ({
          ...prev,
          phone: refreshedData.phone || "",
          unit_number: refreshedData.unit_number || "",
          street_number: refreshedData.street_number || "",
          street_name: refreshedData.street_name || "",
          suburb: refreshedData.suburb || "",
          state: refreshedData.state || "",
          postcode: refreshedData.postcode || "",
        }));
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      const errorMsg = error instanceof Error ? error.message : "Unknown error";
      setErrorMessage(`Failed to update profile: ${errorMsg}`);
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 py-8 pt-24">
        <div className="max-w-3xl mx-auto">
          <Button 
            variant="ghost" 
            className="mb-6"
            onClick={() => navigate('/customer-dashboard')}
          >
            ← Back to Dashboard
          </Button>
          
          <Card>
            <CardHeader>
              <CardTitle>Profile Settings</CardTitle>
            </CardHeader>
            <CardContent>
              {errorMessage && (
                <Alert variant={errorMessage.includes('phone') ? "warning" : "destructive"} className="mb-6">
                  <AlertDescription>
                    {errorMessage}
                    {errorMessage.includes('phone') && (
                      <div className="mt-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setShowPhoneFormatTips(!showPhoneFormatTips)}
                        >
                          {showPhoneFormatTips ? "Hide tips" : "Show phone format tips"}
                        </Button>
                        
                        {showPhoneFormatTips && (
                          <div className="mt-2 text-sm space-y-1 p-2 bg-muted/50 rounded">
                            <p>Try these phone formats:</p>
                            <ul className="list-disc ml-5">
                              <li>Numbers only: 0410694322</li>
                              <li>Only use digits 0-9</li>
                              <li>Remove all spaces, parentheses, and special characters</li>
                              <li>As a last resort, try leaving it blank</li>
                            </ul>
                            <Button 
                              variant="secondary" 
                              size="sm" 
                              className="mt-2"
                              onClick={() => {
                                setProfile(prev => ({ ...prev, phone: sanitizePhoneNumber(prev.phone) }));
                                setShowPhoneFormatTips(false);
                              }}
                            >
                              Auto-format my phone number
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    {!errorMessage.includes('phone') && errorMessage.includes('Failed to update') && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-2" 
                        onClick={() => setErrorMessage("")}
                      >
                        Dismiss
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              )}
              
              {loading && (
                <div className="bg-muted/50 p-2 rounded mb-4 text-center">
                  <p className="text-sm text-muted-foreground">Saving your changes...</p>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={profile.full_name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={profile.email}
                      disabled
                      className="bg-gray-100"
                    />
                    <p className="text-sm text-muted-foreground">
                      Email cannot be changed
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={profile.phone}
                      onChange={handleChange}
                      className="bg-white"
                    />
                    <p className="text-xs text-muted-foreground">
                      Numbers only - other characters will be removed when saving
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-base font-medium mb-3">Delivery Address</h3>
                    <div className="grid grid-cols-6 gap-3">
                      <div className="col-span-2">
                        <Label htmlFor="unit_number" className="text-xs font-medium text-gray-700">Unit Number</Label>
                        <Input
                          id="unit_number"
                          name="unit_number"
                          value={profile.unit_number}
                          onChange={handleChange}
                          placeholder="Optional"
                          className="mt-1 h-8 text-sm bg-white border-gray-200"
                        />
                      </div>

                      <div className="col-span-2">
                        <Label htmlFor="street_number" className="text-xs font-medium text-gray-700">
                          Street Number <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="street_number"
                          name="street_number"
                          value={profile.street_number}
                          onChange={handleChange}
                          required
                          className="mt-1 h-8 text-sm bg-white border-gray-200"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Use hyphen (-) instead of slash (/)
                        </p>
                      </div>

                      <div className="col-span-2">
                        <Label htmlFor="street_name" className="text-xs font-medium text-gray-700">
                          Street Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="street_name"
                          name="street_name"
                          value={profile.street_name}
                          onChange={handleChange}
                          required
                          className="mt-1 h-8 text-sm bg-white border-gray-200"
                        />
                      </div>

                      <div className="col-span-2">
                        <Label htmlFor="suburb" className="text-xs font-medium text-gray-700">
                          Suburb <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="suburb"
                          name="suburb"
                          value={profile.suburb}
                          onChange={handleChange}
                          required
                          className="mt-1 h-8 text-sm bg-white border-gray-200"
                        />
                      </div>

                      <div className="col-span-2">
                        <Label htmlFor="state" className="text-xs font-medium text-gray-700">
                          State <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={profile.state}
                          onValueChange={(value) => setProfile({...profile, state: value})}
                        >
                          <SelectTrigger id="state" className="mt-1 h-8 text-sm bg-white border-gray-200">
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                          <SelectContent>
                            {["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"].map((state) => (
                              <SelectItem key={state} value={state}>{state}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="col-span-2">
                        <Label htmlFor="postcode" className="text-xs font-medium text-gray-700">
                          Postcode <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="postcode"
                          name="postcode"
                          value={profile.postcode}
                          onChange={handleChange}
                          required
                          maxLength={4}
                          className="mt-1 h-8 text-sm bg-white border-gray-200"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : "Save Changes"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ProfileSettings; 